/**
 * YotpoProvider Context
 * 
 * Provides Yotpo state and methods throughout the app via React Context.
 * Wrap your app with this provider to enable Yotpo globally.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useYotpo } from '../hooks/useYotpo';

const YotpoContext = createContext(null);

/**
 * YotpoProvider Component
 * @param {Object} props
 * @param {string} props.appKey - Yotpo app key (required)
 * @param {boolean} props.reinitOnRouteChange - Reinitialize widgets on route change (default: true)
 * @param {number} props.routeChangeDelay - Delay before reinit on route change (default: 300)
 * @param {boolean} props.autoInit - Auto-initialize widgets when ready (default: true)
 * @param {number} props.scriptTimeout - Script load timeout in ms (default: 10000)
 * @param {React.ReactNode} props.children
 */
export const YotpoProvider = ({
  appKey,
  reinitOnRouteChange = true,
  routeChangeDelay = 300,
  autoInit = true,
  scriptTimeout = 10000,
  children,
}) => {
  const yotpoState = useYotpo({
    appKey,
    autoInit,
    scriptTimeout,
    reinitOnMount: false, // We'll handle route changes separately
  });

  const location = useLocation();
  const [lastPath, setLastPath] = useState(location.pathname);

  /**
   * Handle route changes
   */
  useEffect(() => {
    if (!reinitOnRouteChange) return;
    
    // Check if route actually changed
    if (location.pathname !== lastPath) {
      setLastPath(location.pathname);

      // Delay reinitialization to allow new content to render
      const timeoutId = setTimeout(() => {
        if (yotpoState.ready) {
          yotpoState.reinitWidgets();
        }
      }, routeChangeDelay);

      return () => clearTimeout(timeoutId);
    }
  }, [location.pathname, lastPath, reinitOnRouteChange, routeChangeDelay, yotpoState]);

  if (!appKey) {
    console.error('YotpoProvider: appKey is required');
    return children;
  }

  return (
    <YotpoContext.Provider value={yotpoState}>
      {children}
    </YotpoContext.Provider>
  );
};

/**
 * Hook to access Yotpo context
 * @returns {Object} Yotpo state and methods
 * @throws {Error} If used outside YotpoProvider
 */
export const useYotpoContext = () => {
  const context = useContext(YotpoContext);
  
  if (!context) {
    throw new Error('useYotpoContext must be used within a YotpoProvider');
  }
  
  return context;
};

/**
 * Optional: Hook that safely returns null if outside provider
 * Useful if Yotpo is optional in your app
 */
export const useYotpoContextSafe = () => {
  return useContext(YotpoContext);
};

export default YotpoProvider;
